import type React from 'react';

import { Alert, Spinner, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural } from '@change-corgi/core/react/i18n';

import { LoadMoreButton } from 'src/app/pages/petitionDetails/shared/components';
import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';
import { isError, isLoading } from 'src/app/shared/utils/async';

import { usePetitionUpdates } from '../../hooks/updates';
import { SECTION_ID_MAPPING, useScrollToSection } from '../../hooks/useScrollToSection';

import { Update } from './components/Update';
import { NewUpdateCtaContainer } from './NewUpdateCta';

const UpdatesContainerInner: React.FC = () => {
	const updatesState = usePetitionUpdates();
	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	if (isLoading(updatesState)) {
		return <Spinner className="mx-auto my-6" />;
	}

	if (isError(updatesState)) {
		return (
			<Alert className="my-4" variant="error">
				<Translate value="fe.errors.generic_try_again" />
			</Alert>
		);
	}

	const { updates, loadingMore, fetchMore } = updatesState;

	return (
		<div className="mt-[52px]">
			<div ref={containerRef}>
				{/* using role due to https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html */}
				<ul>
					{updates.map((update, index) => {
						const newUpdate = index === 0;

						return (
							<li key={update.id}>
								{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
									<div className="sr-only" role="alert">
										<TranslatePlural
											value="corgi.petition.details.updates.fetched_more"
											count={updates.length - lastEvent.previousItemsCount}
											replacements={{ count: updates.length - lastEvent.previousItemsCount }}
										/>
									</div>
								)}
								<Update update={update} newUpdate={newUpdate} ref={getListItemRef(index)} />
							</li>
						);
					})}
				</ul>
				{loadingMore && (
					<>
						<div className="sr-only" role="alert">
							<Translate value="corgi.petition.details.updates.fetching_more" />
						</div>
						<Spinner className="mx-auto" ref={loadingRef} />
					</>
				)}
				{fetchMore && !loadingMore && (
					<div className="flex items-center justify-center">
						<LoadMoreButton
							onClick={() => {
								recordListEvent({ type: 'loadMore', itemsCount: updates.length });
								fetchMore();
							}}
							ref={loadMoreRef}
							data-qa="petition-updates-load-more"
						>
							<Translate value="corgi.petition.details.updates.view_more_updates_button" />
						</LoadMoreButton>
					</div>
				)}
			</div>
		</div>
	);
};

export const UpdatesContainer: React.FC = () => {
	const {
		data: { refs },
	} = useScrollToSection();

	return (
		<div id={SECTION_ID_MAPPING.UPDATES} ref={refs[SECTION_ID_MAPPING.UPDATES]}>
			<Text className="my-6 mb-0 block text-[40px] sm:text-xl" variant="heading" size="large">
				<Translate value="corgi.petition_gamma.petition_update.update_feed_title" />
			</Text>
			<div className="flex flex-row">
				<div className="flex-1">
					<NewUpdateCtaContainer />
					<UpdatesContainerInner />
				</div>
			</div>
		</div>
	);
};
